import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { callRpc } from './callRpc';
import { initialsOf } from '../format';

export interface SupervisionUnit {
  id: string;
  name: string;
  is_active: boolean;
  supervisor_user_id: string | null;
}

interface ScopeResult {
  isSuperAdmin: boolean;
  units: SupervisionUnit[];
}

export interface SupervisionIdentity {
  user_id: string;
  name: string;
  email: string | null;
  avatar_url: string | null;
  initials: string;
}

export interface SupervisionAccess {
  isSuperAdmin: boolean;
  units: SupervisionUnit[];
  hasAccess: boolean;
  identity: SupervisionIdentity | null;
}

/**
 * Gate da área de Supervisão. Acesso = `is_super_admin` OU supervisor de ≥1 unidade/grupo.
 * `bu_my_supervision_scope()` resolve o escopo no backend (super admin => todas as unidades).
 * Também carrega a identidade do usuário (independente de org) para o rodapé da sidebar.
 */
export function useSupervisionAccess() {
  return useQuery({
    queryKey: ['supervisao', 'access'],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<SupervisionAccess> => {
      const scope = await callRpc<ScopeResult>('bu_my_supervision_scope', {});
      const units = scope?.units ?? [];
      const isSuperAdmin = !!scope?.isSuperAdmin;

      let identity: SupervisionIdentity | null = null;
      const { data: auth } = await supabase.auth.getUser();
      if (auth?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, email, avatar_url')
          .eq('user_id', auth.user.id)
          .maybeSingle();
        const p = (profile ?? {}) as { full_name?: string | null; email?: string | null; avatar_url?: string | null };
        identity = {
          user_id: auth.user.id,
          name: p.full_name || auth.user.email || 'Supervisor',
          email: p.email || auth.user.email || null,
          avatar_url: p.avatar_url || null,
          initials: initialsOf(p.full_name, auth.user.email),
        };
      }

      return { isSuperAdmin, units, hasAccess: isSuperAdmin || units.length > 0, identity };
    },
  });
}
